// EventSystem — triggers random events and pending event chains, and
// resolves the chosen option. Extracted from the old EventsManager
// component; the UI now only renders `pendingEvent` and delegates here.

import { EventBus, GameEvents } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import { EVENT_DEFINITIONS, EventDefinition, EventType, getRandomEvent } from '@/config/eventsConfig'
import { Rng, defaultRng } from '../utils/rng'

const RANDOM_EVENT_CHANCE = 0.3

export function getEventDefinition(type: EventType): EventDefinition | undefined {
  return EVENT_DEFINITIONS.find(e => e.type === type)
}

/**
 * Apply the chosen option of the pending event: cost, reward, persistent
 * effects and follow-up chains. Safe to call from UI and tests.
 */
export function resolveEventOption(optionIndex: number) {
  const store = useGameStore.getState()
  if (!store.pendingEvent) return

  const event = getEventDefinition(store.pendingEvent)
  if (!event) {
    store.setPendingEvent(null)
    return
  }

  const option = event.options[optionIndex]
  if (!option) return

  if (option.costMultiplier && option.costMultiplier > 0) {
    store.subtractMoney(option.costMultiplier, `Evento: ${event.title}`)
  }

  if (option.rewardMultiplier && option.rewardMultiplier > 0) {
    store.addMoney(option.rewardMultiplier, `Evento: ${event.title}`)
  }

  if (option.effects && option.effects.length > 0) {
    for (const effectDef of option.effects) {
      store.addEffect({
        ...effectDef,
        id: `${event.type}-${store.day}-${Math.random().toString(36).slice(2, 8)}`,
        // Definitions with expiresOnDay 0 get a default duration:
        // positive effects last 7 days, negative ones 14.
        expiresOnDay: effectDef.expiresOnDay > 0
          ? effectDef.expiresOnDay
          : store.day + (effectDef.value >= 1 ? 7 : 14),
      })
    }
  }

  if (option.followUpEventId && option.followUpDelay) {
    store.addPendingChain({
      eventId: option.followUpEventId,
      triggerDay: store.day + option.followUpDelay,
    })
  }

  store.setPendingEvent(null)
  EventBus.emit(GameEvents.EVENT_RESOLVED, { event, optionIndex })
}

/** Dismiss the pending event without choosing (backdrop click). */
export function dismissEvent() {
  useGameStore.getState().setPendingEvent(null)
}

export class EventSystem {
  private handler = () => this.checkForEvents()

  constructor(private rng: Rng = defaultRng) {
    EventBus.on(GameEvents.DAY_CHANGED, this.handler)
  }

  destroy() {
    EventBus.off(GameEvents.DAY_CHANGED, this.handler)
  }

  private checkForEvents() {
    const store = useGameStore.getState()

    // Don't stack popups: wait until the current one is resolved
    if (store.pendingEvent) return

    // Pending event chains take priority over random events
    const dueChain = store.pendingEventChains.find(c => store.day >= c.triggerDay)
    if (dueChain) {
      store.removePendingChain(dueChain.eventId)
      const chainEvent = getEventDefinition(dueChain.eventId as EventType)
      if (chainEvent) {
        this.trigger(chainEvent)
      }
      return
    }

    if (this.rng() < RANDOM_EVENT_CHANCE) {
      const event = getRandomEvent(
        store.day,
        store.courts.length,
        store.reputation,
        store.buildings.length,
        store.staff.length,
      )
      if (event) {
        this.trigger(event)
      }
    }
  }

  private trigger(event: EventDefinition) {
    useGameStore.getState().setPendingEvent(event.type)
    EventBus.emit(GameEvents.EVENT_TRIGGERED, { event })
  }
}
